import { state } from '@angular/animations';
import { inject, NgModule } from '@angular/core';
import { PreloadAllModules, Router, RouterModule, Routes } from '@angular/router';

import {
  AcceptInvitationComponent
} from 'auth/pages/accept-invitation/accept-invitation.component';
import { AcceptInvitationResolver } from 'auth/pages/accept-invitation/accept-invitation.resolver';
import { ForgotPasswordComponent } from 'auth/pages/forgot-password/forgot-password.component';
import {
  InvitationInvalidComponent
} from 'auth/pages/invitation-invalid/invitation-invalid.component';
import { LoginBoxComponent } from 'auth/pages/login-box/login-box.component';
import { loginFunctionalGuard } from 'auth/pages/login-box/login-guard';
import { PasswordResetComponent } from 'auth/pages/password-reset/password-reset.component';
import { PasswordResetResolver } from 'auth/pages/password-reset/password-reset.resolver';
import { VerifyMfaComponent } from 'auth/pages/verify-mfa/verify-mfa.component';
import { CampaignHomePage } from 'campaign/pages/campaign-home/campaign-home.page';
import { authGuard } from 'core/guards/auth.guard';
import { mustHaveCampaignFunctionalGuard } from 'core/guards/must-have-campaign-param.guard';
import {
  ADMIN_NO_FOOTER, ADMIN_WITH_FOOTER, CAMPAIGN_NO_FOOTER, CAMPAIGN_WITH_FOOTER
} from 'core/route-data-layouts';
import { AppPersistantStateService, StoreNames } from 'core/state/app-state.service';
import { Campaign, GetCampaignsCountGQL } from 'generated/graphql';
import { isNilOrEmpty } from 'helpers';
import { map } from 'rxjs';

import { Roles } from './constants';
import { restoreCampaignGuard } from './core/guards/restore-campaign.guard';
import { HomeComponent } from './home/home.component';
import { NoCampaignsPage } from './main/pages/no-campaigns/no-campaigns.page';
import { NotFoundPage } from './main/pages/not-found/not-found.page';
import { SelectCampaignPage } from './main/pages/select-campaign/select-campaign.page';
import { AccountSetupMfaPage } from 'account/pages/account-setup-mfa/account-setup-mfa.page';

const ADMIN_ROUTES = {
  path: 'admin',
  data: {},
  canActivate: [authGuard],
  children: [
    {
      path: 'fields',
      data: {
        layout: ADMIN_WITH_FOOTER
      },
      loadChildren: () => import(`./field-admin/field-admin.module`).then((m) => m.FieldAdminModule)
    },
    {
      path: 'upload',
      data: {
        layout: ADMIN_WITH_FOOTER
      },
      loadChildren: () => import(`./upload/upload.module`).then((m) => m.UploadModule)
    },
    {
      path: 'uploader',
      data: {
        layout: ADMIN_WITH_FOOTER
      },
      loadChildren: () => import(`./uploads2/uploads2.module`).then((m) => m.Uploads2Module)
    },
    {
      path: 'account',
      data: {
        layout: ADMIN_WITH_FOOTER
      },
      loadChildren: () => import(`./account/account.module`).then((m) => m.AccountModule)
    },
    {
      path: 'entity-types',
      canActivate: [authGuard],
      data: {
        layout: ADMIN_WITH_FOOTER,
        canActivateRoles: [Roles.administrator]
      },
      loadChildren: () => import(`./entity-types/entity-types.module`).then((m) => m.EntityTypesModule)
    },
    {
      path: 'connection-types',
      canActivate: [authGuard],
      data: {
        layout: ADMIN_WITH_FOOTER,
        canActivateRoles: [Roles.administrator]
      },
      loadChildren: () => import(`./connection-types/connection-types.module`).then((m) => m.ConnectionTypesModule)
    },
    {
      path: 'assessment',
      canActivate: [authGuard],
      data: {
        layout: ADMIN_WITH_FOOTER,
        canActivateRoles: [Roles.administrator]
      },
      loadChildren: () => import(`./assessment/assessment-admin.module`).then((m) => m.AssessmentAdminModule)
    },
    {
      path: 'integrations',
      canActivate: [authGuard],
      data: {
        layout: ADMIN_WITH_FOOTER,
        canActivateRoles: [Roles.administrator]
      },
      loadChildren: () => import(`./integrations/integrations.module`).then((m) => m.IntegrationsModule)
    },
  ]
};

const routes: Routes = [
  {
    path: '',
    canActivate: [
      authGuard,
      restoreCampaignGuard
    ],
    component: HomeComponent,
    children:
      [
        {
          path: 'no-campaigns',
          canActivate: [authGuard],
          component: NoCampaignsPage,
          data: {
            title: 'No Campaigns',
            layout: {
              headerType: 'no_tabs',
              hideFooter: false
            }
          }
        },
        {
          path: 'entity',
          loadChildren: () => import(`./entity/entity.module`).then((m) => m.EntityModule)
        },
        {
          path: 'entity/chart',
          loadChildren: () => import(`./entity-wall-chart/entity-wall-chart.module`).then((m) => m.EntityWallChartModule)
        },
        {
          path: 'entity/visualization',
          loadChildren: () => import(`./entity-visualization/entity-visualization.module`).then((m) => m.EntityVisualizationModule)
        },
        {
          path: 'entity/merge',
          loadChildren: () => import(`./entity-merge/entity-merge.module`).then((m) => m.EntityMergeModule)
        },
        {
          path: 'entity/map-view',
          loadChildren: () => import(`./entity-map-view/entity-map-view.module`).then((m) => m.EntityMapViewModule)
        },
        {
          path: 'activity',
          loadChildren: () => import(`./activity/activity.module`).then((m) => m.ActivityModule)
        },
        {
          path: 'dashboard',
          component: CampaignHomePage,
          canActivate: [authGuard, mustHaveCampaignFunctionalGuard()],
          data: {
            title: 'Campaign Dashboard',
          }
        },
        {
          path: 'notes',
          redirectTo: 'dashboard'
        },
        {
          path: 'action',
          canActivate: [authGuard, mustHaveCampaignFunctionalGuard()],
          loadChildren: () => import(`./action/action.module`).then((m) => m.ActionModule)
        },
        {
          path: 'events',
          canActivate: [authGuard, mustHaveCampaignFunctionalGuard()],
          loadChildren: () => import(`./events/events.module`).then((m) => m.EventsModule)
        },
        // Admin Routes
        ADMIN_ROUTES,
        // Campaign Customize / Admin
        {
          path: 'campaign',
          data: {},
          // canActivate: [authGuard],
          loadChildren: () => import(`./campaign/campaign.module`).then((m) => m.CampaignModule)
        }
      ]
  },
  {
    path: 'print',
    canActivate: [authGuard, restoreCampaignGuard],
    children: [
      {
        path: '',
        data: {
          layout: ADMIN_NO_FOOTER
        },
        loadChildren: () => import(`./print-view/print-view.module`).then((m) => m.PrintViewModule)
      },
    ]
  },
  {
    path: 'select-campaign',
    data: {
      title: 'Select a Campaign',
      layout: {
        headerType: 'no_tabs',
        hideFooter: false
      }
    },
    canActivate: [authGuard,
      // Checks that campaigns are available.
      (_route, _state) => {
      const getCampaignsCountGQL = inject(GetCampaignsCountGQL);
      const router = inject(Router);
      return getCampaignsCountGQL.fetch().pipe(
        map(({ data }) => data?.searchCampaigns?.totalCount),
        map(count => count > 0),
        map(hasCampaigns => {
          if (hasCampaigns) {
            return true;
          }
          return router.parseUrl(`/no-campaigns`)
        })
      )
    }],
    component: HomeComponent,
    children: [
      {
        path: '',
        component: SelectCampaignPage
      },
    ]
  },


  // Account MFA
  // Still requires auth, but does not use the HomeComponent layout.
  {
    path: 'setup_mfa',
    component: AccountSetupMfaPage,
    canActivate: [authGuard],
    data: {
      title: 'Setup 2-Factor Authentication',
    },
  },

  // No auth
  {
    path: 'login',
    component: LoginBoxComponent,
    canActivate: [loginFunctionalGuard()],
    data: { title: 'Login' }
  },
  {
    path: 'verify_mfa',
    component: VerifyMfaComponent,
    // canActivate: [loginFunctionalGuard()],
    data: { title: 'Verify MFA' }
  },
  {
    path: 'forgot_password',
    component: ForgotPasswordComponent,
    data: { title: 'Forgot Password' }
  },
  {
    path: 'reset_password',
    component: PasswordResetComponent,
    resolve: {
      resetData: PasswordResetResolver
    }
  },
  { path: 'invitation_invalid', component: InvitationInvalidComponent },
  {
    path: 'invitation',
    component: AcceptInvitationComponent,
    data: {
      title: 'Accept Invitation'
    },
    resolve: {
      userInviteData: AcceptInvitationResolver
    }
  },
  { path: '**', component: NotFoundPage, data: { title: 'Page Not Found' } }
];

const routerModule = RouterModule.forRoot(routes, {
  enableTracing: false,
  scrollPositionRestoration: 'enabled',
  paramsInheritanceStrategy: 'always',
  preloadingStrategy: PreloadAllModules,
  // relativeLinkResolution: 'legacy'
});
@NgModule({
  imports: [routerModule],
  providers: [],
  exports: [RouterModule]
})
export class AppRoutingModule { }
